
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Search,
  Image as ImageIcon,
  CheckCircle,
  CloudUpload,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { cmsService, CMSMedia } from "../services/cms";
import ImageUpload from "./ImageUpload";

interface MediaLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectImage: (imageUrl: string) => void;
  selectedImages?: string[];
  folder?: string;
}

const MediaLibraryDialog: React.FC<MediaLibraryDialogProps> = ({
  open,
  onClose,
  onSelectImage,
  selectedImages = [],
  folder = "products",
}) => {
  const { t } = useTranslation();
  const [media, setMedia] = useState<CMSMedia[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadMode, setUploadMode] = useState(false);

  useEffect(() => {
    if (open) {
      fetchMedia();
    }
  }, [open]);

  const fetchMedia = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await cmsService.getMedia(folder);
      if (error) throw error;
      setMedia(data || []);
    } catch (err: any) {
      console.error("Error fetching media:", err);
      setError(err.message || "Failed to load media library");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = (imageUrl: string) => {
    // Add the newly uploaded image and select it immediately
    onSelectImage(imageUrl);
    // Refresh the media list
    fetchMedia();
    setUploadMode(false);
  };

  const filteredMedia = media.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.file_name.toLowerCase().includes(query) ||
      item.file_url.toLowerCase().includes(query) ||
      (item.alt_text && item.alt_text.toLowerCase().includes(query)) ||
      (item.caption && item.caption.toLowerCase().includes(query))
    );
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "95%", sm: "90%", md: "85%" },
          height: { xs: "90vh", md: "80vh" },
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <ImageIcon sx={{ color: "#00d4ff" }} />
        {t("admin.mediaLibrary") || "Media Library"}
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1, display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "center" } }}>
        <TextField
          size="small"
          placeholder={t("common.search") || "Search images..."}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant={uploadMode ? "contained" : "outlined"}
            size="small"
            startIcon={<CloudUpload />}
            onClick={() => setUploadMode(!uploadMode)}
            sx={{
              ...(uploadMode
                ? { background: "linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)" }
                : {}),
              whiteSpace: "nowrap",
            }}
          >
            {t("admin.uploadNew") || "Upload New"}
          </Button>
          <Button size="small" onClick={() => fetchMedia()} disabled={loading}>
            {t("common.refresh") || "Refresh"}
          </Button>
        </Box>
      </Box>

      <DialogContent sx={{ flex: 1, overflow: "auto", pt: 2 }}>
        {uploadMode && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "rgba(0,212,255,0.03)", borderRadius: 2, border: "1px solid rgba(0,212,255,0.15)" }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: "#00d4ff" }}>
              {t("admin.uploadNewImage") || "Upload New Image"}
            </Typography>
            <ImageUpload
              currentImage=""
              onImageChange={handleUploadComplete}
              folder={folder}
              label={t("admin.uploadNewImage") || "Upload New Image"}
              maxSize={10}
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredMedia.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              color: "text.secondary",
            }}
          >
            <ImageIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" gutterBottom>
              {t("admin.noImagesFound") || "No images found"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery
                ? (t("common.tryDifferentSearch") || "Try a different search term")
                : (t("admin.uploadImageToGetStarted") || "Upload an image to get started")}
            </Typography>
            {!searchQuery && !uploadMode && (
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => setUploadMode(true)}
                sx={{
                  mt: 2,
                  background: "linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)",
                }}
              >
                {t("admin.uploadNewImage") || "Upload Image"}
              </Button>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                md: "repeat(4, 1fr)",
                lg: "repeat(5, 1fr)",
              },
              gap: { xs: 1, sm: 2 },
            }}
          >
            {filteredMedia.map((item) => {
              const isSelected = selectedImages.includes(item.file_url);

              return (
                <Box
                  key={item.id}
                  onClick={() => onSelectImage(item.file_url)}
                  sx={{
                    position: "relative",
                    aspectRatio: "1 / 1",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "2px solid",
                    borderColor: isSelected ? "#00d4ff" : "divider",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#00d4ff",
                      transform: "scale(1.03)",
                      boxShadow: "0 4px 12px rgba(0,212,255,0.2)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={item.file_url}
                    alt={item.alt_text || item.file_name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/placeholder-product.png";
                    }}
                  />
                  {isSelected && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        bgcolor: "#00d4ff",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 24,
                        height: 24,
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 16, color: "white" }} />
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: 0.5,
                      background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "white",
                        fontSize: "0.65rem",
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.file_name}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
        <Box sx={{ flex: 1 }}>
          {selectedImages.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {selectedImages.length} {t("common.selected") || "selected"}
            </Typography>
          )}
        </Box>
        <Button onClick={onClose}>
          {t("common.close") || "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaLibraryDialog;
